import videojs from 'video.js';
import getParliamentVideoStreamUrl from './getParliamentVideoStreamUrl';
import { fetchStreamStartTimestamp } from './ClipCreation/hlsPlaylist';

// Prototype: 3 offscreen video.js players each grab one still frame from an event stream so
// "Relevant remarks" rows can show the moment as a thumbnail. Jobs queue in mount order (≈ top
// to bottom); a row cancels its job if it unmounts before a worker picks it up. Swap the whole
// thing for a server-rendered frame endpoint if this feels too slow.

const WORKER_COUNT = 3;
const SEEK_TIMEOUT_MS = 6000;
const JOB_TIMEOUT_MS = 25000;
const FRAME_W = 320;
const FRAME_H = 180;
const SEEK_LEAD_CORRECTION_SECONDS = 5; // mirrors ParliamentLiveEventContentVideo.videoSecondsForItem

const frameCache = new Map(); // key -> jpeg dataURL
const streamByEvent = new Map(); // parliamentEventId -> Promise<{ url, startTimestamp }>
const queue = [];
const workers = [];
let started = false;

const keyFor = (parliamentEventId, itemTimestamp) => `${parliamentEventId}:${Math.round(itemTimestamp)}`;

const getStream = (parliamentEventId) => {
  if (!streamByEvent.has(parliamentEventId)) {
    const promise = (async () => {
      const url = await getParliamentVideoStreamUrl(parliamentEventId);
      if (!url) throw new Error('No entitled stream');
      const startTimestamp = await fetchStreamStartTimestamp(url);
      return { url, startTimestamp };
    })();
    promise.catch(() => streamByEvent.delete(parliamentEventId)); // let a later row retry
    streamByEvent.set(parliamentEventId, promise);
  }
  return streamByEvent.get(parliamentEventId);
};

const seekTo = (player, seconds) =>
  new Promise((resolve) => {
    if (Math.abs(player.currentTime() - seconds) < 0.15) return resolve();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const timer = setTimeout(finish, SEEK_TIMEOUT_MS); // a seek past real end never fires 'seeked'
    player.one('seeked', () => {
      clearTimeout(timer);
      requestAnimationFrame(finish);
    });
    player.currentTime(seconds);
  });

const drawFrame = (player) => {
  const videoEl = player.tech({ IWillNotUseThisInPlugins: true }).el();
  const canvas = document.createElement('canvas');
  canvas.width = FRAME_W;
  canvas.height = FRAME_H;
  canvas.getContext('2d').drawImage(videoEl, 0, 0, FRAME_W, FRAME_H);
  return canvas.toDataURL('image/jpeg', 0.7);
};

const createWorker = () => {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  Object.assign(host.style, {
    position: 'fixed',
    left: '-99999px',
    top: '0',
    width: `${FRAME_W}px`,
    height: `${FRAME_H}px`,
    pointerEvents: 'none',
  });
  const videoElement = document.createElement('video-js');
  host.appendChild(videoElement);
  document.body.appendChild(host);
  const player = videojs(videoElement, {
    controls: false,
    muted: true,
    preload: 'auto',
    crossOrigin: 'anonymous',
  });
  return { player, host, currentUrl: null, busy: false };
};

const loadSource = (worker, url) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('source load timeout')), SEEK_TIMEOUT_MS);
    worker.player.one('loadeddata', () => {
      clearTimeout(timer);
      resolve();
    });
    worker.player.one('error', () => {
      clearTimeout(timer);
      reject(new Error('source load error'));
    });
    worker.player.src({ src: url, type: 'application/x-mpegURL' });
  });

const runJob = async (worker, job) => {
  const { url, startTimestamp } = await getStream(job.parliamentEventId);
  if (startTimestamp == null) throw new Error('No stream start timestamp');
  if (worker.currentUrl !== url) {
    await loadSource(worker, url);
    worker.currentUrl = url;
  }
  const second = Math.max(0, job.itemTimestamp - startTimestamp + SEEK_LEAD_CORRECTION_SECONDS);
  await seekTo(worker.player, second);
  const frame = drawFrame(worker.player);
  frameCache.set(job.key, frame);
  return frame;
};

const pump = () => {
  for (const worker of workers) {
    if (worker.busy || !queue.length) continue;
    const job = queue.shift();
    worker.busy = true;
    const guard = setTimeout(() => job.reject(new Error('job timeout')), JOB_TIMEOUT_MS);
    runJob(worker, job)
      .then(job.resolve, job.reject)
      .finally(() => {
        clearTimeout(guard);
        worker.busy = false;
        pump();
      });
  }
};

const ensureStarted = () => {
  if (started) return;
  started = true;
  for (let i = 0; i < WORKER_COUNT; i++) workers.push(createWorker());
};

let refCount = 0;
let teardownTimer = null;

const teardown = () => {
  queue.splice(0).forEach((job) => job.reject(new Error('pool torn down')));
  workers.splice(0).forEach((worker) => {
    try {
      worker.player.dispose();
    } catch (error) {}
    worker.host.remove();
  });
  streamByEvent.clear();
  started = false;
};

// Call on mount of anything that uses the pool; run the returned fn on unmount. The pool is
// disposed a few seconds after the last consumer leaves, so a route bounce doesn't rebuild it.
const retainRemarkFramePool = () => {
  refCount += 1;
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  return () => {
    refCount = Math.max(0, refCount - 1);
    if (refCount === 0 && !teardownTimer) {
      teardownTimer = setTimeout(() => {
        teardownTimer = null;
        if (refCount === 0) teardown();
      }, 3000);
    }
  };
};

// { promise, cancel }. cancel() only does anything while the job is still queued.
const requestRemarkFrame = ({ parliamentEventId, itemTimestamp }) => {
  const key = keyFor(parliamentEventId, itemTimestamp);
  if (frameCache.has(key)) return { promise: Promise.resolve(frameCache.get(key)), cancel: () => {} };
  ensureStarted();
  let job;
  const promise = new Promise((resolve, reject) => {
    job = { key, parliamentEventId, itemTimestamp, resolve, reject };
    queue.push(job);
  });
  pump();
  return {
    promise,
    cancel: () => {
      const i = queue.indexOf(job);
      if (i >= 0) {
        queue.splice(i, 1);
        job.reject(new Error('cancelled'));
      }
    },
  };
};

export { retainRemarkFramePool };
export default requestRemarkFrame;
